import { useTranslation } from "react-i18next";
import i18next from "src/i18n/config";

import { Box, useTheme } from "@mui/material";
import { DangerOutlinedButton } from "@shared/CustomButton";
import { MinimalTableCell } from "@shared/table/MinimalTableComponents";
import { Tooltip } from "@shared/Tooltip";
import { ColumnDef } from "@tanstack/react-table";

const textOrDash = (value: string | null | undefined) => (value?.trim() ? value : "-");

type StatusIconProps = {
    label: string;
    iconClassName: string;
    color: string;
};

const StatusIcon = ({ label, iconClassName, color }: StatusIconProps) => (
    <Tooltip title={label} disableChildrenWrapper>
        <span aria-label={label}>
            <i className={iconClassName} style={{ color }} />
        </span>
    </Tooltip>
);

const StatusCell = ({ account }: { account: API.Account }) => {
    const { t } = useTranslation(["common", "pages"]);
    const theme = useTheme();
    const flagColor = theme.palette.emteria.error;
    const mutedColor = theme.palette.emteria.action;

    return (
        <MinimalTableCell>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {!account.confirmed ? (
                    <StatusIcon
                        label={t("pages:adminAccountsPage.tooltip.notConfirmed")}
                        iconClassName="fa-regular fa-fw fa-clock"
                        color={mutedColor}
                    />
                ) : null}
                {account.commercialUse ? (
                    <StatusIcon
                        label={t("pages:adminAccountsPage.tooltip.commercialUse")}
                        iconClassName="fa-solid fa-fw fa-dollar-sign"
                        color={flagColor}
                    />
                ) : null}
                {account.deleted ? (
                    <StatusIcon
                        label={t("common:label.deleted")}
                        iconClassName="fa-solid fa-fw fa-circle-xmark"
                        color={flagColor}
                    />
                ) : null}
            </Box>
        </MinimalTableCell>
    );
};

type ActionCellProps = {
    account: API.Account;
    isOwnAccount: boolean;
    onDelete: (account: API.Account) => void;
};

const getDeleteAccountTooltip = (account: API.Account, isOwnAccount: boolean) => {
    if (isOwnAccount) {
        return i18next.t("pages:adminAccountsPage.tooltip.cannotDeleteOwnAccount");
    }

    if (account.roles.length > 0) {
        return i18next.t("pages:adminAccountsPage.tooltip.cannotDeleteAccountWithRole", { count: account.roles.length });
    }

    return i18next.t("common:button.delete");
};

const ActionCell = ({ account, isOwnAccount, onDelete }: ActionCellProps) => {
    const isDeleteDisabled = isOwnAccount || account.roles.length > 0;

    return (
        <MinimalTableCell align="right">
            <DangerOutlinedButton
                disabled={isDeleteDisabled}
                onClick={() => onDelete(account)}
                tooltipProps={{
                    title: getDeleteAccountTooltip(account, isOwnAccount),
                }}
            >
                <i className="fa-solid fa-trash-alt" />
            </DangerOutlinedButton>
        </MinimalTableCell>
    );
};

type AccountColumnActions = {
    onDelete: (account: API.Account) => void;
    currentUserId: string | undefined;
};

export const getAccountColumns = ({ onDelete, currentUserId }: AccountColumnActions): ColumnDef<API.Account, string>[] => [
    {
        header: i18next.t("common:label.status"),
        id: "status",
        cell: (info) => <StatusCell account={info.row.original} />,
        enableSorting: false,
    },
    {
        header: i18next.t("common:label.email"),
        accessorKey: "email",
        cell: (info) => <MinimalTableCell>{textOrDash(info.getValue())}</MinimalTableCell>,
        enableSorting: true,
    },
    {
        header: i18next.t("common:label.name"),
        accessorKey: "name",
        cell: (info) => <MinimalTableCell>{textOrDash(info.getValue())}</MinimalTableCell>,
        enableSorting: true,
    },
    {
        header: i18next.t("common:label.country"),
        accessorKey: "country",
        cell: (info) => <MinimalTableCell>{textOrDash(info.getValue())}</MinimalTableCell>,
        // Sorting is server-side (SortCriteria on admin/accounts/all) and the backend only accepts
        // "email" and "name" — a sortable country header would reset to page 1 and reorder nothing.
        enableSorting: false,
    },
    {
        header: i18next.t("common:label.id"),
        accessorKey: "id",
        cell: (info) => <MinimalTableCell>{info.getValue()}</MinimalTableCell>,
        enableSorting: false,
    },
    {
        header: "",
        id: "actions",
        cell: (info) => (
            <ActionCell account={info.row.original} isOwnAccount={info.row.original.id === currentUserId} onDelete={onDelete} />
        ),
        enableSorting: false,
    },
];
